import { Link } from 'react-router-dom';

const locationBlocks = [
  {
    city: 'Wellesley, MA',
    badge: 'Our Home Base',
    desc: 'Our showroom at 326A Washington Street is your destination for the full bespoke experience. Walk-ins welcome, appointments preferred.',
    services: ['Bespoke Suits & Shirts', 'Custom Blazers', 'Alterations', 'Wedding & Formalwear'],
    image: 'https://public.readdy.ai/ai/img_res/9cb4badc582171a1d5782149d95fc1f2.jpg',
  },
  {
    city: 'Boston, MA',
    badge: 'Metro Boston',
    desc: 'We serve Boston professionals with in-store appointments and house or office call visits across Back Bay, Financial District, Beacon Hill, and beyond.',
    services: ['Custom Suits', 'Office Call Visits', 'Alterations', 'Shirt Tailoring'],
    image: 'https://readdy.ai/api/search-image?query=boston%20back%20bay%20brownstone%20row%20houses%20red%20brick%20commonwealth%20avenue%20tree%20lined%20promenade%20autumn%20foliage%20city%20skyline%20blue%20sky%20new%20england%20urban%20neighborhood&width=700&height=480&seq=boston-loc-6631&orientation=landscape',
  },
  {
    city: 'Newton, MA',
    badge: 'Greater Boston',
    desc: 'Newton clients enjoy in-store visits to our Wellesley showroom and house call appointments just minutes from their front door.',
    services: ['Custom Suits', 'House Call Appointments', 'Formalwear', 'Alterations'],
    image: 'https://readdy.ai/api/search-image?query=newton%20massachusetts%20residential%20street%20colonial%20victorian%20homes%20manicured%20lawns%20tree%20canopy%20autumn%20orange%20leaves%20affluent%20suburb%20boston%20sunny%20afternoon&width=700&height=480&seq=newton-loc-7741&orientation=landscape',
  },
  {
    city: 'Cambridge & Brookline',
    badge: 'Metro Area',
    desc: 'From Harvard academics to Coolidge Corner professionals — premium custom tailoring with flexible scheduling across Cambridge and Brookline.',
    services: ['Custom Shirts', 'Bespoke Suits', 'Alterations', 'Casual Tailoring'],
    image: 'https://readdy.ai/api/search-image?query=cambridge%20massachusetts%20harvard%20university%20red%20brick%20buildings%20ivy%20walls%20stone%20pathway%20autumn%20trees%20pedestrians%20historic%20campus%20new%20england%20architecture&width=700&height=480&seq=cambridge-loc-8851&orientation=landscape',
  },
  {
    city: 'MetroWest MA',
    badge: 'Natick · Framingham · Needham',
    desc: 'The MetroWest corridor is firmly within our service area. Clients throughout Natick, Framingham, and Needham trust us for their finest garments.',
    services: ['Bespoke Suits', 'Custom Shirts', 'House Calls', 'Alterations'],
    image: 'https://readdy.ai/api/search-image?query=suburban%20new%20england%20town%20center%20main%20street%20shops%20autumn%20trees%20brick%20sidewalk%20clean%20commercial%20district%20Massachusetts%20metrowest%20daytime%20clear%20blue%20sky&width=700&height=480&seq=metrowest-loc-9962&orientation=landscape',
  },
  {
    city: 'All of Massachusetts',
    badge: 'Statewide Coverage',
    desc: 'From Worcester and Springfield to Cape Cod and the North Shore — if you need custom clothing in MA, our traveling tailors have you covered.',
    services: ['Full Custom Tailoring', 'Office & Home Calls', 'Fabric Selection', 'All Fittings'],
    image: 'https://readdy.ai/api/search-image?query=massachusetts%20new%20england%20coastal%20landscape%20aerial%20view%20ocean%20coastline%20autumn%20foliage%20rolling%20hills%20small%20towns%20cape%20cod%20peninsula%20blue%20atlantic%20water%20scenic%20panorama&width=700&height=480&seq=massachusetts-loc-1073&orientation=landscape',
  },
];

const LocationBlocksSection = () => {
  return (
    <section className="py-32 bg-[#0F0F0F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section Header */}
        <div className="text-center mb-20">
          <p className="text-[#C4A962] font-semibold tracking-widest uppercase text-sm mb-4">
            Where We Serve
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-5">
            Custom Tailoring Across Massachusetts
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Based in Wellesley, we bring bespoke craftsmanship to clients throughout the state —
            in-store and right at your location.
          </p>
        </div>

        {/* Location Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {locationBlocks.map((loc) => (
            <div
              key={loc.city}
              className="group rounded-3xl overflow-hidden border border-white/8 hover:border-[#C4A962]/35 transition-all bg-[#1A1A1A] flex flex-col"
            >
              {/* Image */}
              <div className="relative w-full h-52 overflow-hidden flex-shrink-0">
                <img
                  src={loc.image}
                  alt={`Custom tailoring in ${loc.city}`}
                  className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <span className="text-xs font-bold text-white bg-[#C4A962] px-3 py-1.5 rounded-full tracking-wide">
                    {loc.badge}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-7 flex flex-col flex-1">
                <h3 className="text-lg font-bold text-white mb-3">{loc.city}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-6">{loc.desc}</p>

                <div className="space-y-2.5 mb-7 flex-1">
                  {loc.services.map((s) => (
                    <div key={s} className="flex items-center gap-2.5">
                      <div className="w-5 h-5 bg-[#C4A962]/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-[#C4A962] text-xs"></i>
                      </div>
                      <span className="text-gray-300 text-sm">{s}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 border border-[#C4A962]/40 text-[#C4A962] font-semibold text-sm px-5 py-3 rounded-xl hover:bg-[#C4A962] hover:text-white hover:border-[#C4A962] transition-all cursor-pointer whitespace-nowrap"
                >
                  Book in this area <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA Banner */}
        <div className="mt-16 rounded-3xl overflow-hidden relative">
          <img
            src="https://readdy.ai/api/search-image?query=aerial%20massachusetts%20landscape%20autumn%20foliage%20new%20england%20panoramic%20view%20towns%20forests%20coast%20wide%20cinematic%20drone%20photography&width=1400&height=400&seq=ma-cta-banner-2284&orientation=landscape"
            alt="Massachusetts tailoring service area"
            className="w-full h-56 object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/75 to-black/60"></div>
          <div className="absolute inset-0 flex flex-col md:flex-row items-center justify-between px-10 py-8 gap-6">
            <div>
              <p className="text-[#C4A962] font-semibold tracking-widest uppercase text-xs mb-2">Don't See Your City?</p>
              <h3 className="text-2xl md:text-3xl font-bold text-white">We Serve All of Massachusetts &amp; New England</h3>
              <p className="text-gray-300 text-sm mt-2">Call us to confirm service in your exact area — our tailors travel statewide.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <Link
                to="/contact"
                className="bg-[#C4A962] text-white px-7 py-3.5 rounded-xl font-semibold hover:bg-[#B39952] transition-all whitespace-nowrap text-sm"
              >
                <i className="ri-calendar-line mr-2"></i>Book a Consultation
              </Link>
              <a
                href="tel:781-237-3166"
                className="bg-white/10 border border-white/25 text-white px-7 py-3.5 rounded-xl font-semibold hover:bg-white/15 transition-all whitespace-nowrap text-sm"
              >
                <i className="ri-phone-line mr-2"></i>781-237-3166
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default LocationBlocksSection;
